import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {FormGroup} from '@angular/forms';
import {
  type ErrorPayload,
  type LoadingStatus,
  type TLabels,
  TranslateHelper,
} from '@binotel-frontend/bnt-common-components';
import {API_ERROR_KEY, EApiKnownErrors} from '@entities/api';
import {IApiErrors} from '@entities/api/api.interfaces';
import {FormControlsOf} from '@entities/common/interfaces/forms.interfaces';
import {ESnackbarType} from '@entities/ui/snackbar';
import {environment} from '@envs/environment';
import {SnackbarHelper} from '@shared/helper/snackbar.helper';
import {OdApiErrorResponse} from '@shared/services';
import {deepMap} from '@utils/deep-map.function';

const NOT_FOUND_ERROR_CODES = [
  'validation.exists',
  'validation.integer',
  'subject.notFound',
];

const USER_INPUT_ERROR_CODES = [
  'phone.invalid',
  'telephony.badVerificationCode',
  'telephony.exhaustedVerificationAttempts',
  'telephony.phoneNumberUsedTooOften',
  'user.invalidPassword',
  'user.phoneVerificationExpired',
  'user.phoneVerificationFailed',
];

type TReportableError =
  | Error
  | {errorClass: string; errorMessage: string}
  | {name: string; message: string}
  | string;

export class ErrorsHelper {
  public static error(error: TReportableError): void {
    let message: string;
    if (error instanceof Error) {
      message = error.message;
    } else if (typeof error === 'object') {
      if ('message' in error) {
        message = error.message;
      }
      if ('errorMessage' in error) {
        message = error.errorMessage;
      }
    } else {
      message = error;
    }

    console.error(message, environment.production ? '' : new Error('Stack'));
  }

  public static getApiErrors(
    loadingStatus: LoadingStatus
  ): [string, TLabels[]][] {
    if (!loadingStatus.error) {
      return null;
    }
    return Object.entries(loadingStatus.error as IApiErrors) as [
      string,
      TLabels[],
    ][];
  }

  public static isNotFoundError(loadingStatus: LoadingStatus): boolean {
    const error = loadingStatus.error as ErrorPayload | null;

    if (!error) {
      return false;
    }

    return this.isNotFound(error.httpStatus, error.error);
  }

  public static isNotFoundResponse(
    error: OdApiErrorResponse | HttpErrorResponse
  ): boolean {
    return this.isNotFound(error?.status, error?.error);
  }

  public static isUserInputError(error: unknown): boolean {
    return this.collectErrorCodes(error).some((code) =>
      USER_INPUT_ERROR_CODES.includes(code)
    );
  }

  public static setFormErrors<T>(
    form: FormGroup<FormControlsOf<T>>,
    loadingStatus: LoadingStatus
  ): void {
    const errors = this.getApiErrors(loadingStatus);

    if (!errors) {
      Object.keys(form.controls).forEach((key) => {
        if (form.get(key).hasError(API_ERROR_KEY)) {
          form.get(key).setErrors({[API_ERROR_KEY]: null});
        }
      });
      return;
    }

    const generalApiErrors = (loadingStatus.error as IApiErrors)[
      EApiKnownErrors.GeneralError
    ];
    if (Array.isArray(generalApiErrors) && generalApiErrors.length) {
      SnackbarHelper.open(
        TranslateHelper.translate(generalApiErrors[0]),
        ESnackbarType.Error
      );
    } else {
      SnackbarHelper.open(
        TranslateHelper.translate('common.incorrect-form'),
        ESnackbarType.Error
      );
    }

    errors.forEach(([key, err]) => {
      if (key in form.controls) {
        form.get(key).setErrors({
          [API_ERROR_KEY]: err.map((e) => TranslateHelper.translate(e)),
        });
        form.get(key).markAsTouched();
      }
    });
  }

  public static getErrorLabelsFromIApi(error: OdApiErrorResponse): IApiErrors {
    let apiErrors: IApiErrors = error?.error;
    if (!apiErrors) {
      if (error instanceof OdApiErrorResponse) {
        apiErrors = {
          [EApiKnownErrors.GeneralError]: [error?.message as TLabels],
        };
      } else {
        apiErrors = {
          [EApiKnownErrors.Runtime]: [(error as Error)?.message as TLabels],
        };
      }
    }

    const endpoint =
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      error?.url?.split('/').slice(5).join('/') ?? EApiKnownErrors.Runtime;
    const message = `\nAPI RESPONSE ${endpoint}:\n${this.getApiErrorAsString(apiErrors)}\n`;

    if (error.notCritical) {
      console.warn(message, error.error);
    } else {
      this.error({
        name: `API ERROR: ${endpoint}`,
        message,
      });
    }

    return apiErrors;
  }

  public static getApiErrorAsString(
    errors: Record<
      string,
      string | string[] | Record<string, string | string[]>
    >,
    withFields = true
  ): string {
    if (!errors) {
      return null;
    }

    const errorStrings = Object.entries(this.flattenObject(errors)).map(
      ([key, value]) => {
        const text = TranslateHelper.translate(`${value as TLabels}`);
        return withFields ? `${text} (${key})` : `${text}`;
      }
    );

    return [...new Set(errorStrings)].join('\n');
  }

  public static checkTranslationLabels(errors: IApiErrors): IApiErrors {
    if (!errors) {
      return {};
    }
    return deepMap(errors, (msg) =>
      this.findTranslationLabel(msg as string)
    ) as IApiErrors;
  }

  public static findTranslationLabel(msg: string): TLabels {
    const labels = TranslateHelper.getTranslations();

    if (labels[msg as TLabels]) {
      return msg as TLabels;
    }
    const apiErr: TLabels = `api.${msg}` as TLabels;
    if (labels[apiErr]) {
      return apiErr;
    }

    this.error({
      name: 'API ERROR: TRANSLATION IS MISSING',
      message: msg,
    });

    return msg as TLabels;
  }

  private static isNotFound(
    httpStatus: number | undefined,
    error: unknown
  ): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (httpStatus === HttpStatusCode.NotFound) {
      return true;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (httpStatus !== HttpStatusCode.UnprocessableEntity) {
      return false;
    }

    return this.collectErrorCodes(error).some((code) =>
      NOT_FOUND_ERROR_CODES.includes(code)
    );
  }

  private static collectErrorCodes(error: unknown): string[] {
    if (typeof error === 'string') {
      return [error];
    }

    if (Array.isArray(error)) {
      return error.flatMap((value) => this.collectErrorCodes(value));
    }

    if (error && typeof error === 'object') {
      return Object.values(error).flatMap((value) =>
        this.collectErrorCodes(value)
      );
    }

    return [];
  }

  private static flattenObject(
    data: Record<string, string | string[] | Record<string, string | string[]>>,
    parent?: string
  ): Record<string, string | string[]> {
    return Object.entries(data).reduce(
      (acc, [key, val]) => {
        const newKey = parent ? (key ? `${parent}.${key}` : parent) : key;
        if (typeof val === 'string') {
          acc[newKey] = val;
        } else if (Array.isArray(val)) {
          val.forEach((item, idx) => {
            acc[`${newKey}.${idx}`] = item;
          });
        } else {
          return {
            ...acc,
            ...ErrorsHelper.flattenObject(
              val as Record<string, string>,
              newKey
            ),
          };
        }
        return acc;
      },
      {} as Partial<Record<string, string | string[]>>
    ) as Record<string, string | string[]>;
  }
}
